/**
 * Act 1 Puzzle — Nectar Escort.
 *
 * Canon PR 3 (2026-04-14 companion book) + SPEC-conservatory-rebuild §4:
 * the escort lives inside the Incubation/Irrigation cycle. Glass-Jaw Foxes
 * hunt during Incubation (hot/dry/bright) and are blinded during
 * Irrigation (cool/misty). Nectar-Opossums only come out in the mist.
 *
 * Two-cat rule (design principle #1) — the room itself is the timing
 * mechanic, and each cat owns half of it:
 *   1. Once the Master Mist Regulator is solved, the first cat to reach the
 *      deep warren (x ≥ ESCORT_ENTRY_X) sets off the deep boiler: the room
 *      surges to Incubation, the opossums wake and cower in their burrows,
 *      the foxes hunt.
 *   2. Basil brings the mist back with Turn-Table Touch at the escort's
 *      mist valve. The mist holds for ESCORT_MIST_SECONDS; cranking the
 *      valve again while it's up tops it off. When it runs out the boiler
 *      surges back to Incubation: opossums freeze where they stand and drop
 *      out of the convoy, and Minna must Shadow Slip past any fox that
 *      prowls into her until Basil re-mists the room.
 *   3. In the mist, Minna's Purr coaxes an opossum out (proximity alone no
 *      longer does); it follows her, and a following opossum that reaches
 *      an empty nest hides there. Every nest sits ≥ 6.5 u from every spawn
 *      (capture radius 3.5 u), so each opossum has to be led.
 * Opossums only follow Minna and only her Purr coaxes them; only Basil's
 * paws turn the valve. Four opossums home solves it. Chronos Bubble (once
 * earned) still slows the foxes.
 *
 * This puzzle owns:
 *   - All `Fox` and `Opossum` NPCs
 *   - All `OpossumNest` interactives and the escort mist valve
 *   - Two `ConservatoryCheckpoint` respawn zones (Canon PR 3)
 *   - The escort's surge / mist-window timing (it asks ConservatoryCycle
 *     for flips; the cycle itself has no timer)
 *   - Per-frame fox/opossum AI tick, fox detection and bites
 *   - Nest capture (opossum enters -> `hide()` + `acceptOpossum()`)
 *   - Chronos Bubble time-scale application on foxes
 *
 * The escort always ends in Irrigation (nests only accept opossums while
 * the mist is up, and the timer stops once the last one is home), so the
 * Sundew apex starts in the same room state it always has.
 */
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Scene } from "@babylonjs/core/scene";

import { Fox } from "../../../entities/npcs/Fox";
import { Opossum } from "../../../entities/npcs/Opossum";
import { OpossumNest } from "../../../entities/interactives/OpossumNest";
import { ValveCrank } from "../../../entities/interactives/ValveCrank";

import type { ChronosBubble } from "../../../abilities/coop/ChronosBubble";
import type { ConservatoryCycle } from "../../../systems/ConservatoryCycle";

import { GameEvents } from "../../../config/events";
import { COLORS, NPC_TUNING, OVERGROWN_CONSERVATORY } from "../../../config/GameConfig";
import { evaluateFail, type FailInput, type FailReason } from "../../../systems/flow/FailEvaluator";
import type { CueDef } from "../../../ui/tutorialCues";

import type { PuzzleContext, PuzzleCue, PuzzleModule, PuzzleState } from "../../PuzzleModule";
import type { PuzzleQAHints } from "../../qa/PuzzleQAHints";

interface FoxDef {
  key: string;
  pos: Vector3;
  patrol: [number, number];
}

export interface OpossumDef {
  key: string;
  pos: Vector3;
}

export interface NestDef {
  key: string;
  pos: Vector3;
}

interface CheckpointDef {
  key: string;
  pos: Vector3;
}

/**
 * Minimal respawn marker. The puzzle owns its lifetime and registers it as
 * an AuraSight interactive so the player can discover the checkpoint while
 * scouting the route. The `PLAYER_DAMAGED` handler reads the most recently
 * touched checkpoint via `getRespawnPosition()`.
 */
export interface ConservatoryCheckpoint {
  key: string;
  position: Vector3;
  mesh: Mesh;
}

const FOX_DEFS: FoxDef[] = [
  // Canon PR 3 adds an opening-corridor patrol at x=30..45 so the
  // player meets the glass jaw threat before the escort section
  // starts. Gives the cycle flip real stakes when Basil first touches
  // the master regulator: the player can see a fox walk nearby, flip
  // to Irrigation, and watch the fox go blind right in front of them.
  { key: "fox_opener", pos: new Vector3(37, 2, 0), patrol: [30, 45] },
  // Guards the east nests (the last leg of the convoy): blind in the
  // mist, it prowls straight through Minna's path once the mist burns
  // off. Kept east of x≈125: Basil's way past the vine ladder is to climb
  // it and jump off the top rung, which lands him at x≈122-124, and his
  // valve (x=114.5) must stay clear of it too.
  { key: "fox_0", pos: new Vector3(128, 2, 0), patrol: [126, 130.5] },
  { key: "fox_1", pos: new Vector3(142, 2, 0), patrol: [136, 146] },
  // Prowls the upper vine-bridge (escortSupportUpperB, top y≈9.2) next to
  // the elevated opossum_1 → nest_1 pair, so the high route has stakes
  // during Incubation.
  { key: "fox_2", pos: new Vector3(125, 9, 0), patrol: [122, 128] },
];

/**
 * Burrows (spawns) and nests. Every nest is ≥ 6.5 u from every spawn on
 * its level, so an opossum travels ≥ 3 u after Minna coaxes it before any
 * nest's 3.5 u capture zone can take it — the opossums no longer deliver
 * themselves. Ground NPCs ride at y=2, the walkway pair at y=8.
 */
export const OPOSSUM_DEFS: readonly OpossumDef[] = [
  { key: "opossum_0", pos: new Vector3(104.5, 2, 0) },
  { key: "opossum_1", pos: new Vector3(111.8, 8, 0) },
  { key: "opossum_2", pos: new Vector3(118.5, 2, 0) },
  { key: "opossum_3", pos: new Vector3(120, 2, 0) },
];

export const NEST_DEFS: readonly NestDef[] = [
  { key: "nest_0", pos: new Vector3(112, 2, 0) },
  { key: "nest_1", pos: new Vector3(118.4, 8, 0) },
  // The grease cache at x=128 sits in the gap between the east nests.
  { key: "nest_2", pos: new Vector3(126.5, 2, 0) },
  { key: "nest_3", pos: new Vector3(129.5, 2, 0) },
];

const CHECKPOINT_DEFS: CheckpointDef[] = [
  { key: "checkpoint_0", pos: new Vector3(60, 3, 0) },
  { key: "checkpoint_1", pos: new Vector3(110, 3, 0) },
];

/** Either cat at or past this x (once the regulator is solved) sets off the deep boiler. */
export const ESCORT_ENTRY_X = 98;
/** The escort mist valve Basil cranks (ground, under walkway A). */
export const ESCORT_VALVE_POS = new Vector3(114.5, 1.3, -2.1);
export const ESCORT_VALVE_KEY = "escortMistValve";
/** Seconds the mist holds after Basil's crank before the boiler surges back. */
export const ESCORT_MIST_SECONDS = 35;
/** Warn this many seconds before the mist burns off. */
const MIST_THIN_WARNING_S = 10;
/** Basil's Turn-Table Touch reach to the valve (world units). */
const VALVE_REACH = 4;
/** A hunting fox this close to Minna (x, same level) cues Shadow Slip; clears past the release distance. */
const FOX_THREAT_X = 4;
const FOX_THREAT_RELEASE_X = 6;
const FOX_THREAT_Y = 2.5;

// Cue chips (module constants: the HUD rebuilds a chip only when the
// object changes).
const CUE_APPROACH: CueDef = { cat: "both", parts: [{ text: "to the burrows" }] };
const CUE_VALVE: CueDef = {
  cat: "basil",
  parts: [{ action: "turnStart" }, { text: "crank," }, { action: "rhythmHit" }, { text: "on the beat" }],
};
const CUE_COAX: CueDef = { cat: "minna", parts: [{ action: "purr", hold: true }, { text: "coax it out" }] };
const CUE_LEAD: CueDef = { cat: "minna", parts: [{ text: "lead it home" }] };
const CUE_SLIP: CueDef = { cat: "minna", parts: [{ action: "shadow" }, { text: "slip past foxes" }] };
export const NECTAR_ESCORT_CUES = {
  approach: CUE_APPROACH,
  valve: CUE_VALVE,
  coax: CUE_COAX,
  lead: CUE_LEAD,
  slip: CUE_SLIP,
} as const;

export type NectarEscortStep =
  | "idle" // regulator not solved yet
  | "approach" // head into the deep warren
  | "valve" // Incubation: Basil brings the mist
  | "misting" // the flip to Irrigation is under way
  | "hide" // Incubation and a hunting fox is on Minna
  | "coax" // mist up, nobody following: Minna purrs one out
  | "lead" // mist up, an opossum follows: lead it to a nest
  | "done";

/** Horizontal distance at which a following opossum counts as entering a nest. */
const NEST_CAPTURE_RADIUS = NPC_TUNING.OPOSSUM.FOLLOW_DISTANCE + 1.0;
/** Vertical slack for the 2.5D nest check so harmless Y drift cannot reject capture. */
const NEST_CAPTURE_VERTICAL_RADIUS = 1.5;
/** Distance at which a chasing fox scatters a visible opossum. */
const FOX_SCATTER_RADIUS = 2;
/** Distance at which a hunting fox bites a detectable cat. */
const FOX_BITE_RADIUS = 1.4;
/** Per-fox cooldown between bites (ms of scene time). */
const FOX_BITE_COOLDOWN_MS = 2_500;
/** Distance at which a cat is considered "on" a respawn checkpoint. */
const CHECKPOINT_TOUCH_RADIUS = 1.4;
/**
 * The lanterns hang at y=3 over a floor whose cats stand at y≈0.7, so a
 * 3-D radius never reached them from the floor: a cat walked right under
 * checkpoint_1 without lighting it, and a fox bite in the escort sent it
 * back to the regulator. A cat touches a lantern within
 * CHECKPOINT_TOUCH_RADIUS along x and this far below/above it.
 */
const CHECKPOINT_TOUCH_HEIGHT = 3;

/** Resting emissive of a lantern checkpoint's warm glass core. Kept
 *  ≤ ~0.45 per channel so the GlowLayer doesn't beacon it (Q-037). */
const LANTERN_CORE_EMISSIVE = new Color3(0.42, 0.26, 0.08);
/** Emissive of the ACTIVE lantern (last-touched respawn target) — the
 *  physical "lit lantern" cue, 1.4x the resting glow. */
const LANTERN_CORE_EMISSIVE_LIT = LANTERN_CORE_EMISSIVE.scale(1.4);

export function isInsideOpossumNestCaptureZone(
  opossumPos: { x: number; y: number },
  nestPos: { x: number; y: number },
): boolean {
  return (
    Math.abs(opossumPos.x - nestPos.x) <= NEST_CAPTURE_RADIUS &&
    Math.abs(opossumPos.y - nestPos.y) <= NEST_CAPTURE_VERTICAL_RADIUS
  );
}

/**
 * Escort-lost fail decision. Delegates to the shared `evaluateFail` so the
 * branch logic stays single-sourced; the escort puzzle only owns the
 * `escortLost` predicate (a scattered, non-reclaimable opossum).
 */
export function shouldFailEscort(i: FailInput): FailReason {
  return evaluateFail(i);
}

export class NectarEscortPuzzle implements PuzzleModule {
  readonly id = "nectarEscort";
  readonly displayName = "Nectar Escort";

  private ctx!: PuzzleContext;
  private chronosBubble: ChronosBubble;
  private conservatoryCycle: ConservatoryCycle;

  private foxes: Fox[] = [];
  private opossums: Opossum[] = [];
  private nests: OpossumNest[] = [];
  private checkpoints: ConservatoryCheckpoint[] = [];
  /** Brass cage material shared by every lantern checkpoint. */
  private lanternBrassMat: StandardMaterial | null = null;
  private alertedFoxes = new Set<string>();

  private opossumsSafe = 0;
  private escortComplete = false;
  /** One-shot guard: prevents duplicate PUZZLE_FAILED emits from a rapid
   *  PLAYER_DAMAGED burst. Reset by CHECKPOINT_RESTORED so the player can
   *  fail again after returning from checkpoint. */
  private escortFailEmitted = false;

  // Default to the first checkpoint so a damage event during the opening
  // fox patrol has a sensible fallback before either cat has touched a
  // marker.
  private activeCheckpointKey: string = CHECKPOINT_DEFS[0].key;

  // ── Escort flow (see the header) ──
  private mistValve!: ValveCrank;
  /** Valve currently gripped by Basil's Turn-Table Touch. */
  private valveActive = false;
  /** The Master Mist Regulator is solved: the escort can begin. */
  private armed = false;
  /** A cat reached the deep warren: the opossums are awake. */
  private started = false;
  /** The entry surge to Incubation still has to be accepted by the cycle. */
  private surgePending = false;
  /** The mist ran out: the surge back to Incubation is still to be accepted. */
  private driftPending = false;
  /** The escort's mist is up (Irrigation from the valve, not transitioning away). */
  private mistUp = false;
  private mistRemaining = 0;
  private thinWarned = false;
  /** Direction of the cycle transition in flight (null when settled). */
  private transitionTo: "incubation" | "irrigation" | null = null;
  /** The mist has come up at least once (cue wording). */
  private everMisted = false;
  /** Hunting fox currently on Minna (hysteresis between FOX_THREAT_X and FOX_THREAT_RELEASE_X). */
  private threatFox: Fox | null = null;

  constructor(chronosBubble: ChronosBubble, conservatoryCycle: ConservatoryCycle) {
    this.chronosBubble = chronosBubble;
    this.conservatoryCycle = conservatoryCycle;
  }

  setup(ctx: PuzzleContext): void {
    this.ctx = ctx;
    const { scene } = ctx;

    this.foxes = FOX_DEFS.map((fd) => new Fox(scene, fd.key, fd.pos, fd.patrol));
    this.opossums = OPOSSUM_DEFS.map((od) => new Opossum(scene, od.key, od.pos));
    this.nests = NEST_DEFS.map(
      (nd) => new OpossumNest(scene, { key: nd.key, position: nd.pos, label: nd.key }),
    );
    this.checkpoints = CHECKPOINT_DEFS.map((cd) => this.createCheckpoint(scene, cd));
    this.mistValve = new ValveCrank(scene, {
      key: ESCORT_VALVE_KEY,
      position: ESCORT_VALVE_POS.clone(),
      variant: "irrigation",
      label: "Escort Mist Valve",
    });

    ctx.registerInteractive({
      mesh: this.mistValve.rootMesh,
      highlightTarget: this.mistValve.getHighlightTarget(),
      hidden: false,
      state: "revealed",
    });

    for (const nest of this.nests) {
      ctx.registerInteractive({
        mesh: nest.rootMesh,
        hidden: false,
        state: "revealed",
      });
    }

    // ── Arming: the escort waits for the Master Mist Regulator ──
    this.armed = ctx.levelState.get("masterValve")?.solved === true;
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PUZZLE_SOLVED, (data) => {
        if (data?.puzzleId === "masterValve") this.armed = true;
      }),
    );

    // ── Basil's valve: Turn-Table Touch hits, Minna's Purr priming ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.TURN_TABLE_TOUCH_HIT, () => {
        if (this.valveActive) this.mistValve.onInteract("turnTableTouch");
      }),
    );
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_CYCLE_REQUESTED, (data) => {
        if (data.sourceKey !== ESCORT_VALVE_KEY) return;
        this.onValveCranked();
      }),
    );

    // ── Minna's Purr: coax opossums out (mist only) + prime the valve ──
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PURR_PULSE, ({ sourcePosition, radius }) => {
        const valve = this.mistValve;
        if (!valve.isPrimed && !valve.isActivated) {
          const src = new Vector3(sourcePosition.x, sourcePosition.y, sourcePosition.z);
          if (Vector3.Distance(src, valve.rootMesh.position) <= radius) valve.onInteract("calmingResonance");
        }
        if (!this.mistUp || this.escortComplete) return;
        const { minna } = this.ctx;
        for (const opossum of this.opossums) {
          if (opossum.isHidden || opossum.isSleeping || opossum.isFrozen) continue;
          if (opossum.behavior.state === "following") continue;
          const dx = opossum.mesh.position.x - sourcePosition.x;
          const dy = opossum.mesh.position.y - sourcePosition.y;
          if (dx * dx + dy * dy <= radius * radius) {
            opossum.coax(minna.mesh.position.x, minna.mesh.position.y);
          }
        }
      }),
    );

    for (const checkpoint of this.checkpoints) {
      ctx.registerInteractive({
        mesh: checkpoint.mesh,
        hidden: false,
        state: "revealed",
      });
    }

    // Count opossums as they reach nests. The nest itself triggers the hide
    // animation via `opossum.hide()`, which emits OPOSSUM_HIDDEN.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.OPOSSUM_HIDDEN, () => {
        if (this.escortComplete) return;

        this.opossumsSafe++;
        if (this.opossumsSafe >= OVERGROWN_CONSERVATORY.PUZZLE.OPOSSUMS_TO_ESCORT) {
          this.escortComplete = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_SOLVED, { puzzleId: this.id });
        }
      }),
    );

    // Opossums freeze the moment the canonical phase event fires rather
    // than waiting for a settle — if we deferred, a cat who rode the flip
    // mid-convoy would get a grace window where opossums were still
    // dutifully following while foxes were already hunting at full
    // detection radius, and the convoy would be devoured instantly.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_PHASE_CHANGED, (data) => {
        this.transitionTo = null;
        this.applyPhaseState(data.phase);
        // A settled flip releases the valve (like the master regulator).
        this.mistValve.resetCycle();
        this.releaseValve();
        if (data.phase === "irrigation") {
          if (this.started && !this.escortComplete) this.raiseMist();
        } else {
          this.mistUp = false;
        }
      }),
    );

    // Canon PR 3: a cycle *transition* (not yet a settled phase) startles
    // every fox for its default 400ms window. That's the "yip and freeze"
    // beat that signals to players "the environment just changed, the
    // predators can't react for a moment." Ties the drama to the
    // room-flip even when the escort is mid-step. A transition toward
    // Incubation also ends the mist at once: nests stop taking opossums
    // while the heat rolls in.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CONSERVATORY_CYCLE_TRANSITION, (data) => {
        for (const fox of this.foxes) fox.startleFor();
        this.transitionTo = data.to;
        if (data.to === "incubation") this.mistUp = false;
      }),
    );

    // Phase 5 light-fail: a damage event during the escort can knock the
    // convoy below the recoverable threshold. We evaluate escort-lost
    // through the shared `evaluateFail` (harness=intact here; harness-zero
    // is owned by HarnessWoundSystem) and emit PUZZLE_FAILED reason:'escort'
    // so BaseLevel reloads the last checkpoint.
    //
    // One-shot guard: a single physics frame can burst multiple PLAYER_DAMAGED
    // events. `escortFailEmitted` blocks the second and subsequent emits until
    // CHECKPOINT_RESTORED re-arms it (see subscription below).
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.PLAYER_DAMAGED, () => {
        if (this.escortComplete || this.escortFailEmitted) return;
        const reason = shouldFailEscort({
          harnessIntegrity: 1, // harness-zero is handled by HarnessWoundSystem
          escortLost: this.isEscortLost(),
        });
        if (reason === "escort") {
          this.escortFailEmitted = true;
          ctx.gameEvents.emit(GameEvents.PUZZLE_FAILED, {
            puzzleId: this.id,
            reason: "escort",
          });
        }
      }),
    );

    // Re-arm the one-shot guard after a checkpoint restore so the escort can
    // fail again in a fresh attempt.
    ctx.addUnsub(
      ctx.gameEvents.on(GameEvents.CHECKPOINT_RESTORED, () => {
        this.escortFailEmitted = false;
      }),
    );

    this.applyPhaseState(this.conservatoryCycle.canonicalPhase);
  }

  update(dt: number, sceneTime: number): void {
    const { minna, basil, shadowSlip } = this.ctx;

    // Checkpoint proximity — either cat touching a marker updates the
    // respawn target. Runs every frame regardless of cycle phase because
    // the player can get knocked off platforms during either state.
    this.updateCheckpointTouches();

    // Entry surge, mist timer, drift back to Incubation, valve reach.
    this.updateEscortFlow(dt);

    const isIncubation = this.conservatoryCycle.isIncubation;
    const canonicalPhase = this.conservatoryCycle.canonicalPhase;

    if (isIncubation && !this.escortComplete) {
      const minnaStealthed = shadowSlip.isActive;

      // Fox detection of opossums and Minna.
      //
      // Solo-mode fix: opossums that are actively following a stealthed
      // Minna inherit her Shadow Slip protection. Previously the third
      // arg to `fox.canDetect` for opossum checks was hardcoded `false`
      // (opossum is never stealthed), which meant Tab-swapping to Basil
      // while Minna held stealth left her parked-and-stealthed but her
      // opossum followers fully exposed — any passing fox would scatter
      // them and the puzzle would soft-lock. Propagating stealth to the
      // convoy matches the canonical design (Nectar-Opossums hide in
      // mist, which functionally maps to Minna's stealth state) and
      // unblocks solo play without changing co-op difficulty.
      for (const fox of this.foxes) {
        if (fox.isDormant) continue;
        let detectedVisibleOpossum = false;

        for (const opossum of this.opossums) {
          if (opossum.isHidden || opossum.isSleeping || opossum.isFrozen) continue;
          const opossumExposed =
            opossum.behavior.state === "following" ||
            opossum.behavior.state === "fleeing";
          if (!opossumExposed) continue;

          const followerStealthed =
            opossum.behavior.state === "following" && minnaStealthed;

          if (fox.canDetect(
            opossum.mesh.position.x,
            opossum.mesh.position.y,
            followerStealthed,
          )) {
            detectedVisibleOpossum = true;
            fox.startHunting({
              x: opossum.mesh.position.x,
              y: opossum.mesh.position.y,
            });

            const foxOpDist = Vector3.Distance(fox.mesh.position, opossum.mesh.position);
            if (foxOpDist < FOX_SCATTER_RADIUS) {
              opossum.scatter();
            }
          }
        }

        if (!detectedVisibleOpossum && fox.behavior.state === "following") {
          fox.resumePatrol();
        }

        // Fox detects Minna (if she's not stealthed).
        if (fox.canDetect(minna.mesh.position.x, minna.mesh.position.y, minnaStealthed)) {
          if (!this.alertedFoxes.has(fox.npcKey)) {
            this.alertedFoxes.add(fox.npcKey);
            this.ctx.gameEvents.emit(GameEvents.FOX_ALERT, { foxKey: fox.npcKey });
          }
        } else {
          this.alertedFoxes.delete(fox.npcKey);
        }

        // Fox bite — the teeth behind the sight cones. The prompt has
        // always warned "use Shadow Slip (E) to pass fox sight cones",
        // and the puzzle ships a full PLAYER_DAMAGED → escort-lost fail
        // pipeline, but no fox ever emitted damage: the predators were
        // pure theater and stealth was never actually required. A
        // hunting fox that closes to bite range on a detectable cat now
        // nips them back to the last checkpoint (same respawn funnel as
        // the Sundew tongue), with a per-fox cooldown so one contact
        // frame can't chain-bite.
        this.maybeBite(fox, "minna", minna.mesh.position, minnaStealthed, sceneTime);
        this.maybeBite(fox, "basil", basil.mesh.position, false, sceneTime);
      }
    }

    // Opossum follow-chain advancement only runs while the escort's mist
    // is up. The opossum's `checkFollow` already short-circuits in
    // Incubation, but gating here also keeps nest capture out of the hot
    // phase and out of the heat rolling back in.
    if (this.mistUp && !this.escortComplete) {
      for (const opossum of this.opossums) {
        if (opossum.isHidden || opossum.isSleeping || opossum.isFrozen) continue;

        opossum.checkFollow(
          minna.mesh.position.x,
          minna.mesh.position.y,
          shadowSlip.isActive,
          canonicalPhase,
        );

        for (const nest of this.nests) {
          if (nest.isOccupied) continue;
          if (
            opossum.behavior.state === "following" &&
            isInsideOpossumNestCaptureZone(opossum.mesh.position, nest.rootMesh.position)
          ) {
            opossum.hide();
            nest.acceptOpossum();
            break;
          }
        }
      }
    }

    // NPC update ticks (always called — they short-circuit while sleeping).
    // Feed Minna's stealth probe into each fox's sight-cone visualiser so
    // the "cone fails to bite" beat renders when Shadow Slip is active.
    // Semantic detection is still driven by `fox.canDetect(..., isStealthed)`
    // above — the visualiser only adds feedback, never gameplay.
    const minnaStealthedProbe = shadowSlip.isActive
      ? minna.mesh.position
      : null;
    for (const fox of this.foxes) {
      fox.syncSightCone(minnaStealthedProbe, shadowSlip.isActive);
      fox.update(sceneTime, dt);
    }
    for (const opossum of this.opossums) opossum.update(sceneTime, dt);

    // Chronos Bubble time-scale application on foxes.
    if (this.chronosBubble.isActive) {
      this.applyChronosBubbleEffects();
    }
  }

  onAbilityKey(key: string, pressed: boolean, _released: boolean): boolean {
    // 1 = grip / release the escort mist valve with Turn-Table Touch.
    if (key === "1" && pressed) {
      if (this.valveActive) {
        this.releaseValve();
        return true;
      }
      if (!this.basilInValveReach()) return false;
      if (!this.started) {
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The mist valve is dry — the Master Mist Regulator has to run first.",
        });
        return true;
      }
      this.ctx.turnTableTouch.setTarget(this.mistValve.rootMesh.position, () => {});
      if (this.ctx.basilAbilities.activateAbility("turnTableTouch")) {
        this.valveActive = true;
      }
      return true;
    }
    // 2 = rhythm hit on the gripped valve.
    if (key === "2" && pressed && this.valveActive) {
      this.ctx.turnTableTouch.attemptHit();
      return true;
    }
    return false;
  }

  getState(): PuzzleState {
    if (this.escortComplete) return { solved: true, progress: 1 };
    return {
      solved: false,
      progress: Math.min(
        1,
        this.opossumsSafe / OVERGROWN_CONSERVATORY.PUZZLE.OPOSSUMS_TO_ESCORT,
      ),
    };
  }

  /** Current step of the escort (drives getCue; see NectarEscortStep). */
  getStep(): NectarEscortStep {
    if (this.escortComplete) return "done";
    if (!this.armed) return "idle";
    if (!this.started) return "approach";
    if (this.mistUp) {
      return this.opossums.some((o) => !o.isHidden && o.behavior.state === "following") ? "lead" : "coax";
    }
    if (this.transitionTo === "irrigation" && this.conservatoryCycle.isTransitioning) return "misting";
    return this.threatFox ? "hide" : "valve";
  }

  getCue(): PuzzleCue | null {
    const step = this.getStep();
    const thin = this.mistUp && this.mistRemaining <= MIST_THIN_WARNING_S;
    switch (step) {
      case "approach":
        return { objective: "Head for the opossum burrows", cue: CUE_APPROACH, anchor: above(this.mistValve.rootMesh.position, 2.0) };
      case "valve":
        return {
          objective: this.everMisted ? "The mist burned off — re-crank" : "Mist the burrows",
          cue: CUE_VALVE,
          anchor: above(this.mistValve.rootMesh.position, 2.0),
        };
      case "misting":
        return { objective: "The mist is rolling in…", cue: null };
      case "hide": {
        const fox = this.threatFox;
        return { objective: "Hide from the Glass-Jaw fox", cue: CUE_SLIP, anchor: fox ? above(fox.mesh.position, 1.2) : undefined };
      }
      case "coax": {
        const target = this.nearestCoaxable();
        return {
          objective: thin ? "Mist thinning — hurry!" : "Coax an opossum out",
          cue: CUE_COAX,
          anchor: target ? above(target.mesh.position, 1.2) : undefined,
        };
      }
      case "lead": {
        const nest = this.leadTargetNest();
        return {
          objective: thin ? "Mist thinning — hurry!" : "Lead it to its nest",
          cue: CUE_LEAD,
          anchor: nest ? above(nest.rootMesh.position, 1.2) : undefined,
        };
      }
      default:
        return null;
    }
  }

  getInternals() {
    return {
      id: this.id,
      displayName: this.displayName,
      foxes: this.foxes,
      opossums: this.opossums,
      nests: this.nests,
      checkpoints: this.checkpoints,
      mistValve: this.mistValve,
      opossumsSafe: this.opossumsSafe,
      escortComplete: this.escortComplete,
      armed: this.armed,
      started: this.started,
      mistUp: this.mistUp,
      mistRemaining: this.mistRemaining,
      step: this.getStep(),
      chronosBubble: this.chronosBubble,
      conservatoryCycle: this.conservatoryCycle,
      ctx: this.ctx,
    };
  }

  getQAHints(): PuzzleQAHints {
    // Upper-tier stepping-stone platforms that lead from the ground-floor
    // checkpoint_1 up to opossum_1 / nest_1 (y≈8). Positions mirror
    // the stair-step platforms created in OvergrownConservatory.ts —
    // authored as QATargets so the recorder can walkTo each stone in
    // sequence instead of time-based jumping.
    const stepUps = [
      { id: "step_upper_left_0", x: 103.0, y: 2.5 },
      { id: "step_upper_left_1", x: 105.4, y: 4.1 },
      { id: "step_upper_left_2", x: 107.8, y: 5.7 },
      { id: "step_upper_left_3", x: 110.2, y: 7.3 },
    ];

    return {
      puzzleId: this.id,
      successEvent: GameEvents.PUZZLE_SOLVED,
      defaultStart: { minna: "checkpoint_0", basil: "checkpoint_0" },
      targets: [
        // Respawn checkpoints — primary safe zones and default-start anchors.
        ...CHECKPOINT_DEFS.map<{
          id: string;
          position: { x: number; y: number };
          radius: number;
          allowedCat: "either";
          label: string;
        }>((c) => ({
          id: c.key,
          position: { x: c.pos.x, y: c.pos.y },
          radius: CHECKPOINT_TOUCH_RADIUS,
          allowedCat: "either",
          label: `Checkpoint ${c.key}`,
        })),

        // Basil's escort mist valve (Turn-Table Touch: 1, then 2 on the beat).
        {
          id: "escort_valve",
          position: { x: ESCORT_VALVE_POS.x, y: ESCORT_VALVE_POS.y },
          radius: 1.2,
          allowedCat: "basil" as const,
          label: "Escort mist valve",
        },

        // Opossum spawn positions — in the mist, Minna purrs (F) near one
        // to coax it out, then leads it to a nest.
        ...OPOSSUM_DEFS.map((o) => ({
          id: `opossum_spawn_${o.key.replace("opossum_", "")}`,
          position: { x: o.pos.x, y: o.pos.y },
          radius: 1.2,
          allowedCat: "minna" as const,
          label: `Opossum spawn ${o.key}`,
        })),

        // Nests — where Minna leads each opossum. Nest meshes are named
        // via OpossumNest's `label: nd.key`; prefer meshName when present.
        ...NEST_DEFS.map((n) => ({
          id: n.key,
          meshName: n.key,
          position: { x: n.pos.x, y: n.pos.y },
          radius: NEST_CAPTURE_RADIUS,
          allowedCat: "minna" as const,
          label: `Nest ${n.key}`,
        })),

        // Fox patrol landmarks — used as reference positions (e.g. "walk
        // until past fox_opener") rather than destinations.
        ...FOX_DEFS.map((f) => ({
          id: `${f.key}_patrol`,
          position: { x: f.pos.x, y: f.pos.y },
          radius: 2,
          allowedCat: "either" as const,
          label: `Fox patrol center ${f.key}`,
        })),

        // Upper-tier stepping stones — let the recorder walk the
        // staircase instead of timing jumps open-loop.
        ...stepUps.map((s) => ({
          id: s.id,
          position: { x: s.x, y: s.y },
          radius: 0.8,
          allowedCat: "minna" as const,
          label: `Upper-tier step ${s.id}`,
        })),
      ],
      traversals: [
        // Stair-stone climb between consecutive upper-tier steps.
        // Each step-up is ~2u east + 2.5u up — within normal-jump
        // peak rise (2.7u) when launched at MOVE_SPEED=5. Recipe
        // pattern: walk-up, jump, continue east briefly. Generic
        // single-hop recipe — chain it via repeated `traverse` calls
        // for each step in the column.
        {
          id: "stairstone_step_up",
          kind: "jump",
          cat: "minna",
          from: "step_upper_left_0",
          to: "step_upper_left_1",
          successWindowMs: 3_000,
          recipe: [
            { keyDown: "d" },
            { waitMs: 400 },
            { keyDown: " " },
            { waitMs: 80 },
            { keyUp: " " },
            { waitMs: 500 },
            { keyUp: "d" },
          ],
        },
      ],
    };
  }

  /** Scene-time (seconds) after which each fox may bite again. */
  private nextBiteAllowedAt = new Map<string, number>();

  private maybeBite(
    fox: Fox,
    cat: "minna" | "basil",
    catPos: Vector3,
    isStealthed: boolean,
    sceneTime: number,
  ): void {
    if (sceneTime < (this.nextBiteAllowedAt.get(fox.npcKey) ?? 0)) return;
    // A braced Pneumatic Anchor is bite-proof — the glass jaw can't crack
    // the brass harness. Matches ShadowRelay's rule so anchor-immunity is
    // consistent everywhere foxes have teeth.
    if (cat === "basil") {
      const anchor = this.ctx.basilAbilities.getAbility("pneumaticAnchor");
      if (anchor?.isActive === true) return;
    }
    if (!fox.canDetect(catPos.x, catPos.y, isStealthed)) return;
    if (Vector3.Distance(fox.mesh.position, catPos) > FOX_BITE_RADIUS) return;

    this.nextBiteAllowedAt.set(fox.npcKey, sceneTime + FOX_BITE_COOLDOWN_MS / 1000);
    this.ctx.gameEvents.emit(GameEvents.PLAYER_DAMAGED, {
      cat,
      source: "foxBite",
      severity: "light",
    });
    // Recoil beat: the fox freezes briefly, so it reads as a lunge-and-
    // recover instead of a stationary hazard.
    fox.startleFor();

    const level = (this.ctx.scene.metadata as {
      level?: {
        respawn: (
          which: "minna" | "basil",
          target?: { x: number; y: number; z: number },
          reason?: "fall" | "fail" | "restore",
        ) => void;
      };
    }).level;
    // Position write through the shared respawn funnel — resolves to the
    // last touched escort checkpoint via the SaveSystem snapshot.
    level?.respawn(cat, undefined, "fail");
  }

  /** Resolve the respawn target for the `PLAYER_DAMAGED` handler. */
  getRespawnPosition(): Vector3 {
    const active =
      this.checkpoints.find((c) => c.key === this.activeCheckpointKey) ??
      this.checkpoints[0];
    return active.position.clone();
  }

  /**
   * The convoy is unrecoverable when a scattered (fleeing) opossum exists AND
   * the still-reachable opossums (hidden ones are already safe; fleeing ones
   * cannot rejoin this run) can no longer meet the escort quota.
   */
  private isEscortLost(): boolean {
    const required = OVERGROWN_CONSERVATORY.PUZZLE.OPOSSUMS_TO_ESCORT;
    const scattered = this.opossums.some(
      (o) => !o.isHidden && o.behavior.state === "fleeing",
    );
    if (!scattered) return false;
    const reachable = this.opossums.filter(
      (o) => o.isHidden || o.behavior.state !== "fleeing",
    ).length;
    return this.opossumsSafe + reachable < required;
  }

  /**
   * Sub-puzzle state seeder. Shape:
   *   {
   *     opossumsSafe?: number,   // opossums already delivered (0..required)
   *     solved?: boolean,        // hide every required opossum + PUZZLE_SOLVED
   *   }
   *
   * Useful seeds:
   *   - `{ solved: true }` for traces testing the downstream Sundew apex
   *     (it's gated on `nectarEscort.solved === true`).
   *   - `{ opossumsSafe: 3 }` so a trace only has to land the final
   *     opossum in its nest to hit the solve.
   */
  seed(state: Record<string, unknown>): void {
    const required = OVERGROWN_CONSERVATORY.PUZZLE.OPOSSUMS_TO_ESCORT;
    let targetCount = -1;
    if (state.solved === true) {
      targetCount = required;
    } else if (typeof state.opossumsSafe === "number") {
      targetCount = Math.max(0, Math.min(required, Math.floor(state.opossumsSafe)));
    }
    if (targetCount < 0) return;

    // Mirror the per-opossum "enter nest" side effect: hide the opossum
    // (which emits OPOSSUM_HIDDEN — caught by our own handler to bump
    // `opossumsSafe` and fire PUZZLE_SOLVED at the quota) and mark its
    // matching nest as occupied. Pair by index so each seeded opossum has
    // a distinct nest, matching the real-play 1-opossum-per-nest shape.
    let seeded = 0;
    const pairs = Math.min(targetCount, this.opossums.length, this.nests.length);
    for (let i = 0; i < pairs && seeded < targetCount; i++) {
      const opossum = this.opossums[i];
      const nest = this.nests[i];
      if (opossum.isHidden || nest.isOccupied) continue;
      opossum.hide();
      nest.acceptOpossum();
      seeded += 1;
    }
  }

  /**
   * `forceSolvePuzzle` (QA fast-forward, checkpoint restore): end the
   * escort as the real solve would — every opossum home, the mist timer
   * stopped — so no entry surge or drift fires into the Sundew apex.
   */
  onForceSolve(): void {
    // Mark complete first: the OPOSSUM_HIDDEN events below are then
    // ignored instead of re-emitting PUZZLE_SOLVED.
    this.escortComplete = true;
    this.armed = true;
    this.started = true;
    this.surgePending = false;
    this.driftPending = false;
    this.opossumsSafe = OVERGROWN_CONSERVATORY.PUZZLE.OPOSSUMS_TO_ESCORT;
    this.releaseValve();
    for (const [i, opossum] of this.opossums.entries()) {
      if (opossum.isHidden) continue;
      opossum.hide();
      const nest = this.nests[i];
      if (nest && !nest.isOccupied) nest.acceptOpossum();
    }
  }

  dispose(): void {
    this.releaseValve();
    this.mistValve.dispose();
    for (const fox of this.foxes) fox.dispose();
    for (const opossum of this.opossums) opossum.dispose();
    for (const nest of this.nests) nest.dispose();
    // dispose(false, true): recurse into the brass cage children AND
    // dispose the per-checkpoint core material (previously leaked).
    for (const checkpoint of this.checkpoints) checkpoint.mesh.dispose(false, true);
    // Shared brass cage material — dispose once (Babylon material
    // dispose is idempotent, so the recursive pass above touching it
    // first is harmless).
    this.lanternBrassMat?.dispose();
    this.lanternBrassMat = null;
  }

  // ─────────────────────────────────────────────────────────────────

  /**
   * Lantern restyle (Q-023 remainder): the old teal debug spheres read
   * as QA leftovers. The checkpoint is now a hanging brass lantern —
   * a warm glass core (the root mesh, so the registerInteractive and
   * position-math contracts are unchanged) inside a brass cage: top and
   * bottom caps, a hanging ring, and two vertical ribs. The core's
   * emissive stays ≤ ~0.45 so the GlowLayer doesn't turn it into a
   * beacon (Q-037 lesson).
   */
  private createCheckpoint(scene: Scene, def: CheckpointDef): ConservatoryCheckpoint {
    const mesh = MeshBuilder.CreateSphere(
      `conservatoryCheckpoint_${def.key}`,
      { diameter: 0.34, segments: 12 },
      scene,
    );
    mesh.position.copyFrom(def.pos);

    const mat = new StandardMaterial(`conservatoryCheckpoint_${def.key}_mat`, scene);
    mat.diffuseColor = Color3.FromHexString("#ffb347");
    mat.emissiveColor = LANTERN_CORE_EMISSIVE.clone();
    mat.specularColor = Color3.Black();
    mesh.material = mat;
    mesh.isPickable = false;

    // One brass material shared by every lantern's cage parts.
    if (!this.lanternBrassMat) {
      this.lanternBrassMat = new StandardMaterial("conservatoryLanternBrassMat", scene);
      this.lanternBrassMat.diffuseColor = Color3.FromHexString(COLORS.BRASS);
      this.lanternBrassMat.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.08);
      this.lanternBrassMat.specularColor = Color3.Black();
    }
    const brass = this.lanternBrassMat;

    const topCap = MeshBuilder.CreateCylinder(
      `conservatoryCheckpoint_${def.key}_topCap`,
      { diameter: 0.3, height: 0.1 },
      scene,
    );
    topCap.parent = mesh;
    topCap.position = new Vector3(0, 0.24, 0);

    const bottomCap = MeshBuilder.CreateCylinder(
      `conservatoryCheckpoint_${def.key}_bottomCap`,
      { diameter: 0.24, height: 0.06 },
      scene,
    );
    bottomCap.parent = mesh;
    bottomCap.position = new Vector3(0, -0.22, 0);

    const ring = MeshBuilder.CreateTorus(
      `conservatoryCheckpoint_${def.key}_ring`,
      { diameter: 0.14, thickness: 0.03 },
      scene,
    );
    ring.parent = mesh;
    ring.position = new Vector3(0, 0.34, 0);

    const ribs: Mesh[] = [];
    for (const [index, xOffset] of [0.15, -0.15].entries()) {
      const rib = MeshBuilder.CreateBox(
        `conservatoryCheckpoint_${def.key}_rib_${index}`,
        { width: 0.03, height: 0.44, depth: 0.03 },
        scene,
      );
      rib.parent = mesh;
      rib.position = new Vector3(xOffset, 0, 0);
      ribs.push(rib);
    }

    for (const part of [topCap, bottomCap, ring, ...ribs]) {
      part.material = brass;
      part.isPickable = false;
    }

    return { key: def.key, position: def.pos.clone(), mesh };
  }

  private updateCheckpointTouches(): void {
    const { minna, basil } = this.ctx;
    for (const checkpoint of this.checkpoints) {
      const touches = (p: Vector3): boolean =>
        Math.abs(p.x - checkpoint.position.x) < CHECKPOINT_TOUCH_RADIUS &&
        Math.abs(p.y - checkpoint.position.y) < CHECKPOINT_TOUCH_HEIGHT;
      if (touches(minna.mesh.position) || touches(basil.mesh.position)) {
        if (this.activeCheckpointKey !== checkpoint.key) {
          this.activeCheckpointKey = checkpoint.key;
          // Physical "lit lantern" cue: brighten the touched lantern's
          // core, dim the others back to resting glow.
          for (const cp of this.checkpoints) {
            const coreMat = cp.mesh.material as StandardMaterial | null;
            coreMat?.emissiveColor.copyFrom(
              cp.key === checkpoint.key ? LANTERN_CORE_EMISSIVE_LIT : LANTERN_CORE_EMISSIVE,
            );
          }
          // Tell BaseLevel's checkpoint capture to snapshot here, so the
          // respawn resolver returns the player to this marker instead of
          // the last puzzle-solve position (see installCheckpointCapture).
          this.ctx.gameEvents.emit(GameEvents.CHECKPOINT_TOUCHED, {
            checkpointKey: checkpoint.key,
          });
        }
      }
    }
  }

  private applyChronosBubbleEffects(): void {
    const bubble = this.chronosBubble.getBubbleData();
    if (!bubble) return;

    const bubblePos = new Vector3(bubble.x, bubble.y, bubble.z);
    for (const fox of this.foxes) {
      const dist = Vector3.Distance(fox.mesh.position, bubblePos);
      if (dist <= bubble.radius) {
        fox.setTimeScale(bubble.timeScale);
      } else {
        fox.resetTimeScale();
      }
    }
  }

  private applyPhaseState(phase: "incubation" | "irrigation"): void {
    if (phase === "incubation") {
      this.alertedFoxes.clear();
      for (const fox of this.foxes) fox.enterHunting();
    } else {
      for (const fox of this.foxes) fox.enterBlinded();
    }
    // The opossums sleep in their burrows until the escort begins.
    if (!this.started) return;
    for (const opossum of this.opossums) {
      if (opossum.isHidden) continue;
      if (opossum.isSleeping) opossum.wake();
      if (phase === "incubation") opossum.freeze();
      else opossum.unfreeze();
    }
  }

  // ── Escort flow ────────────────────────────────────────────────────

  private updateEscortFlow(dt: number): void {
    const { minna, basil } = this.ctx;
    const cycle = this.conservatoryCycle;

    // Basil walked away from the valve: let go of the rhythm ring.
    if (this.valveActive && !this.basilInValveReach()) this.releaseValve();

    if (this.escortComplete) {
      this.surgePending = false;
      this.driftPending = false;
      this.threatFox = null;
      return;
    }

    // 1. Entry: the first cat into the deep warren sets off the boiler.
    if (!this.armed && this.ctx.levelState.get("masterValve")?.solved === true) this.armed = true;
    if (this.armed && !this.started &&
        (minna.mesh.position.x >= ESCORT_ENTRY_X || basil.mesh.position.x >= ESCORT_ENTRY_X)) {
      this.startEscort();
    }

    // 2. The mist window (burning off queues the drift below).
    if (this.mistUp) {
      this.mistRemaining = Math.max(0, this.mistRemaining - dt);
      if (!this.thinWarned && this.mistRemaining <= MIST_THIN_WARNING_S) {
        this.thinWarned = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The mist is thinning — Basil, back to the valve!",
        });
      }
      if (this.mistRemaining <= 0) {
        this.mistUp = false;
        this.driftPending = true;
        this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "The boiler surges — the mist burns off!",
        });
        this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: "mistBurnedOff" });
      }
    }

    // 3. Surge / drift to Incubation: keep asking until the cycle accepts
    // (it refuses mid-transition and during its hysteresis lockout).
    if (this.surgePending || this.driftPending) {
      if (cycle.isIncubation && !cycle.isTransitioning) {
        this.surgePending = false;
        this.driftPending = false;
      } else if (!cycle.isTransitioning && cycle.requestPhaseFlip("incubation")) {
        this.surgePending = false;
        this.driftPending = false;
      }
    }

    // 4. Which hunting fox (if any) is on Minna — drives the Shadow Slip cue.
    this.threatFox = this.findThreatFox();
  }

  private startEscort(): void {
    this.started = true;
    const cycle = this.conservatoryCycle;
    // The opossums wake cowering: nothing comes out in the heat.
    for (const opossum of this.opossums) {
      if (opossum.isHidden) continue;
      if (opossum.isSleeping) opossum.wake();
      opossum.freeze();
    }
    if (!(cycle.isIncubation && !cycle.isTransitioning)) this.surgePending = true;
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "The deep boiler surges — the burrows run hot. Basil: the mist valve!",
    });
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: "escortStarted" });
  }

  /** Irrigation settled while the escort runs: the mist window (re)opens. */
  private raiseMist(): void {
    this.mistUp = true;
    this.everMisted = true;
    this.mistRemaining = ESCORT_MIST_SECONDS;
    this.thinWarned = false;
    this.driftPending = false;
    this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: "mistUp" });
  }

  /** The valve finished a crank (CONSERVATORY_CYCLE_REQUESTED from it). */
  private onValveCranked(): void {
    const cycle = this.conservatoryCycle;
    if (this.started && !this.escortComplete && this.mistUp && cycle.isIrrigation && !cycle.isTransitioning) {
      // Mist already up: the crank tops it off instead of flipping.
      this.mistRemaining = ESCORT_MIST_SECONDS;
      this.thinWarned = false;
      this.mistValve.resetCycle();
      this.releaseValve();
      this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, { message: "Basil tops up the mist." });
      this.ctx.gameEvents.emit(GameEvents.PUZZLE_PROGRESSED, { puzzleId: this.id, step: "mistTopUp" });
      return;
    }
    if (cycle.requestPhaseFlip("irrigation")) return;
    this.mistValve.resetCycle();
    this.ctx.gameEvents.emit(GameEvents.HUD_TOAST, {
      message: "The mist valve hisses but holds — the room is still shifting.",
    });
  }

  private releaseValve(): void {
    if (!this.valveActive) return;
    this.valveActive = false;
    this.ctx.basilAbilities.deactivateAbility("turnTableTouch");
  }

  private basilInValveReach(): boolean {
    return Vector3.Distance(this.ctx.basil.mesh.position, this.mistValve.rootMesh.position) < VALVE_REACH;
  }

  /** Awake, uncoaxed opossum nearest Minna (the coax cue's anchor). */
  private nearestCoaxable(): Opossum | null {
    const m = this.ctx.minna.mesh.position;
    let best: Opossum | null = null;
    let bestD = Infinity;
    for (const o of this.opossums) {
      if (o.isHidden || o.isSleeping || o.isFrozen || o.behavior.state === "following") continue;
      const d = Math.hypot(o.mesh.position.x - m.x, o.mesh.position.y - m.y);
      if (d < bestD) { best = o; bestD = d; }
    }
    return best;
  }

  /** Empty nest on the leading opossum's level nearest to it (the lead cue's anchor). */
  private leadTargetNest(): OpossumNest | null {
    const lead = this.opossums.find((o) => !o.isHidden && o.behavior.state === "following");
    if (!lead) return null;
    let best: OpossumNest | null = null;
    let bestD = Infinity;
    for (const nest of this.nests) {
      if (nest.isOccupied) continue;
      if (Math.abs(nest.rootMesh.position.y - lead.mesh.position.y) > NEST_CAPTURE_VERTICAL_RADIUS) continue;
      const d = Math.abs(nest.rootMesh.position.x - lead.mesh.position.x);
      if (d < bestD) { best = nest; bestD = d; }
    }
    return best;
  }

  /**
   * A hunting fox on Minna's level within FOX_THREAT_X of her while she
   * isn't slipping (kept until it's FOX_THREAT_RELEASE_X away, so the cue
   * doesn't flicker as the fox paces).
   */
  private findThreatFox(): Fox | null {
    if (!this.started || this.mistUp || !this.conservatoryCycle.isIncubation) return null;
    if (this.ctx.shadowSlip.isActive) return null;
    const m = this.ctx.minna.mesh.position;
    const near = (fox: Fox, dx: number) =>
      !fox.isDormant &&
      Math.abs(fox.mesh.position.y - m.y) <= FOX_THREAT_Y &&
      Math.abs(fox.mesh.position.x - m.x) <= dx;
    if (this.threatFox && near(this.threatFox, FOX_THREAT_RELEASE_X)) return this.threatFox;
    return this.foxes.find((fox) => near(fox, FOX_THREAT_X)) ?? null;
  }
}

function above(p: Vector3, dy: number): { x: number; y: number; z: number } {
  return { x: p.x, y: p.y + dy, z: p.z };
}
